import {
  AggregateScores,
  DirectionalTotals,
  InboundAudioFields,
  InboundVideoFields,
  NetworkTotals,
  OutboundAudioFields,
  OutboundVideoFields,
  RttSource,
  StatsGraph,
  TrackScore,
  WorstTrack,
} from 'utils/webrtc/stats/types';

const SECONDS_TO_MS = 1000;

const minOrNull = (values: ReadonlyArray<number | null>): number | null => {
  let min: number | null = null;
  for (const v of values) {
    if (v !== null && (min === null || v < min)) {
      min = v;
    }
  }
  return min;
};

const maxOrNull = (values: ReadonlyArray<number | null>): number | null => {
  let max: number | null = null;
  for (const v of values) {
    if (v !== null && (max === null || v > max)) {
      max = v;
    }
  }
  return max;
};

// Median over non-null entries; null if none. Even count → mean of the two middle
// samples (smooth response when a track drops in/out, vs stepping).
const medianOrNull = (values: ReadonlyArray<number | null>): number | null => {
  const observed: number[] = [];
  for (const v of values) {
    if (v !== null) {
      observed.push(v);
    }
  }
  if (observed.length === 0) {
    return null;
  }
  observed.sort((a, b) => a - b);
  const mid = Math.floor(observed.length / 2);
  if (observed.length % 2 === 1) {
    return observed[mid];
  }
  return (observed[mid - 1] + observed[mid]) / 2;
};

// Degrades to the non-null side when one is empty: listener-only →
// median(receiving), broadcast-only → worst sending.
const composeIndicatorCandidate = (
  worstPublishing: number | null,
  medianReceiving: number | null
): number | null => {
  if (worstPublishing === null) {
    return medianReceiving;
  }
  if (medianReceiving === null) {
    return worstPublishing;
  }
  return Math.min(worstPublishing, medianReceiving);
};

export function aggregateTrackScores(scores: readonly TrackScore[]): AggregateScores {
  const audio = scores.filter((s) => s.kind === 'audio').map((s) => s.score);
  const video = scores.filter((s) => s.kind === 'video').map((s) => s.score);
  const receiving = scores.filter((s) => s.direction === 'receiving').map((s) => s.score);
  const sendingVideo = scores
    .filter((s) => s.direction === 'sending' && s.kind === 'video')
    .map((s) => s.score);

  // Camera and screenshare publish on separate PCs whose mids collide ("0"/"1"),
  // so the group key is feedId-qualified; null feedId falls back to one shared bucket.
  const sendingVideoBySource = new Map<string, (number | null)[]>();
  for (const s of scores) {
    if (s.direction === 'sending' && s.kind === 'video') {
      const groupKey = `${s.feedId ?? 'unresolved'}|${s.mid}`;
      const group = sendingVideoBySource.get(groupKey) ?? [];
      group.push(s.score);
      sendingVideoBySource.set(groupKey, group);
    }
  }
  const bestLayerPerVideoMid = Array.from(sendingVideoBySource.values()).map(maxOrNull);

  const sendingAudio = scores
    .filter((s) => s.direction === 'sending' && s.kind === 'audio')
    .map((s) => s.score);

  const worstPublishing = minOrNull([...sendingAudio, ...bestLayerPerVideoMid]);
  const medianReceiving = medianOrNull(receiving);

  return {
    worstAudio: minOrNull(audio),
    worstVideo: minOrNull(video),
    worstPublishing,
    worstReceiving: minOrNull(receiving),
    // Sending-video-only comparator for Phase-1 parallel-log validation vs legacyMOS.
    publishingVideoMOS: minOrNull(sendingVideo),
    aggregateMOS: minOrNull(scores.map((s) => s.score)),
    medianReceiving,
    aggregateMOSMedianReceiving: composeIndicatorCandidate(worstPublishing, medianReceiving),
  };
}

// Ties resolve to the first track encountered.
export function findWorstTrack(scores: readonly TrackScore[]): WorstTrack | null {
  let worst: WorstTrack | null = null;
  for (const s of scores) {
    if (s.score !== null && (worst === null || s.score < worst.score)) {
      worst = {
        kind: s.kind,
        direction: s.direction,
        feedId: s.feedId,
        mid: s.mid,
        rid: s.rid,
        score: s.score,
      };
    }
  }
  return worst;
}

const meanOrNull = (values: readonly number[]): number | null => {
  if (values.length === 0) {
    return null;
  }
  const sum = values.reduce((acc, value) => acc + value, 0);
  return sum / values.length;
};

// Prefer per-track RTT (finer, where the SFU emits RTCP SR+RR/DLRR — typically
// Chrome publisher-side) over the transport-level candidate-pair RTT (universal
// but per-PC not per-track).
const resolveRtt = (
  trackRttMsValues: readonly number[],
  transportFallbackMs: number | null
): { rttMs: number | null; rttSource: RttSource } => {
  if (trackRttMsValues.length > 0) {
    return { rttMs: meanOrNull(trackRttMsValues), rttSource: 'track' };
  }
  if (transportFallbackMs !== null) {
    return { rttMs: transportFallbackMs, rttSource: 'transport' };
  }
  return { rttMs: null, rttSource: null };
};

const lossPctFromCounters = (packets: number, packetsLost: number): number => {
  const total = packets + packetsLost;
  if (total === 0) {
    return 0;
  }
  return Math.round((packetsLost / total) * 100);
};

type DirectionalRollup = {
  packets: number;
  packetsLost: number;
  jitterMsValues: number[];
  rttMsValues: number[];
};

const emptyRollup = (): DirectionalRollup => ({
  packets: 0,
  packetsLost: 0,
  jitterMsValues: [],
  rttMsValues: [],
});

const collectFieldSamples = (
  fields: OutboundAudioFields | OutboundVideoFields | InboundAudioFields | InboundVideoFields,
  rollup: DirectionalRollup
): void => {
  if (Number.isFinite(fields.jitter)) {
    rollup.jitterMsValues.push(fields.jitter * SECONDS_TO_MS);
  }
  if (fields.roundTripTime !== undefined) {
    rollup.rttMsValues.push(fields.roundTripTime * SECONDS_TO_MS);
  }
};

const finalize = (
  rollup: DirectionalRollup,
  transportFallbackMs: number | null
): DirectionalTotals | null => {
  const observedAnything =
    rollup.packets > 0 || rollup.packetsLost > 0 || rollup.jitterMsValues.length > 0;
  if (!observedAnything) {
    return null;
  }
  const { rttMs, rttSource } = resolveRtt(rollup.rttMsValues, transportFallbackMs);
  return {
    packets: rollup.packets,
    packetsLost: rollup.packetsLost,
    packetLossPct: lossPctFromCounters(rollup.packets, rollup.packetsLost),
    jitterMs: meanOrNull(rollup.jitterMsValues) ?? 0,
    rttMs,
    rttSource,
  };
};

export function aggregateNetworkTotals(graphs: readonly StatsGraph[]): NetworkTotals {
  const sending = emptyRollup();
  const receiving = emptyRollup();
  const transportRttsMs: number[] = [];

  for (const graph of graphs) {
    if (graph.transportRoundTripTime !== null) {
      transportRttsMs.push(graph.transportRoundTripTime * SECONDS_TO_MS);
    }

    graph.cumulativeByTrackKey.forEach((counters) => {
      if (counters.packetsSent !== undefined) {
        sending.packets += counters.packetsSent;
        sending.packetsLost += counters.remotePacketsLost ?? 0;
      } else if (counters.packetsReceived !== undefined) {
        receiving.packets += counters.packetsReceived;
        receiving.packetsLost += counters.packetsLost ?? 0;
      }
    });

    graph.outboundAudio.forEach((fields) => collectFieldSamples(fields, sending));
    graph.outboundVideo.forEach((fields) => collectFieldSamples(fields, sending));
    graph.inboundAudio.forEach((fields) => collectFieldSamples(fields, receiving));
    graph.inboundVideo.forEach((fields) => collectFieldSamples(fields, receiving));
  }

  const transportFallbackMs = meanOrNull(transportRttsMs);

  return {
    sending: finalize(sending, transportFallbackMs),
    receiving: finalize(receiving, transportFallbackMs),
  };
}
